import React, { useState, useEffect } from 'react';
import './style.scss';
import { motion } from 'framer-motion';
import JUNOButton from '../JUNOButton';
import { addEventListener, removeEventListenerByScope } from '../../helpers/pubNubFunctions';
import BarLoader from 'react-spinners/BarLoader';

interface SendToBreakoutsLoaderProps {
  onFinished: () => void;
}

const SendToBreakoutsLoader: React.FC<SendToBreakoutsLoaderProps> = ({ onFinished }) => {
  const [isShowing, setIsShowing] = useState(true);
  const [totalRooms, setTotalRooms] = useState(0);
  const [roomsRecieved, setRoomsRecieved] = useState(0);
  const [error, setError] = useState<boolean | string>(false);
  let autoCloseTimeout;

  useEffect(() => {
    addEventListener(
      PubNubCenter.ON_BREAKOUTS_STARTED,
      (event, message) => {
        setIsShowing(true);
        setTotalRooms(message.message.num_rooms);
      },
      'send_to_breakouts_loader_listeners',
    );

    addEventListener(
      PubNubCenter.ON_MOD_RESPONSE,
      () => {
        setRoomsRecieved(old => old + 1);
      },
      'send_to_breakouts_loader_listeners',
    );

    return () => {
      removeEventListenerByScope(PubNubCenter.ON_BREAKOUTS_STARTED, 'send_to_breakouts_loader_listeners');
      removeEventListenerByScope(PubNubCenter.ON_MOD_RESPONSE, 'send_to_breakouts_loader_listeners');
    };
  }, []);

  useEffect(() => {
    if (roomsRecieved !== totalRooms && totalRooms !== 0) {
      const timeout = setTimeout(() => {
        setError(CrowdHub_App.LANG.BREAKOUTS_CREATION_ERROR);
      }, CrowdHub_App.CONFIG.BREAKOUTS_LOADING_ERROR_TIME_MS);
      return () => {
        clearTimeout(timeout);
      };
    }

    if (roomsRecieved == totalRooms && totalRooms !== 0) {
      //we're done, set autoclose timeout
      autoCloseTimeout = setTimeout(() => {
        onClose();
      }, 3000);
    }
  }, [roomsRecieved]);

  const onClose = () => {
    if (autoCloseTimeout) {
      clearTimeout(autoCloseTimeout);
      autoCloseTimeout = null;
    }

    setIsShowing(false);
    onFinished();
  };

  const closeButton = {
    id: 'close_stb_loading_btn',
    textstyle: '',
    buttonStyle: 'juno_btn_component juno_button juno_mini_button_outline juno_text_lowercase',
    //@ts-ignore
    text: CrowdHub_App.LANG.BREAKOUTS_DISMISS_LOADING,
    onClick: () => {
      onClose();
    },
  };

  const brandColor = getComputedStyle(document.documentElement).getPropertyValue('--brand-color');

  const isFinished = totalRooms > 0 && roomsRecieved == totalRooms;

  return (
    <motion.div
      className={`stb_loading_wrapper ${isFinished && 'finished'}`}
      animate={isShowing ? 'visible' : 'hidden'}
      variants={wrapperVariants}
    >
      {!isFinished && <BarLoader color={brandColor} width={`100%`} />}
      {totalRooms === 0 && <h4>{CrowdHub_App.LANG.BREAKOUTS_PREPARING}</h4>}
      {totalRooms > 0 && (
        <>
          <h4>{CrowdHub_App.LANG.BREAKOUTS_CREATING}</h4>
          {error && <p>{error}</p>}
          <div className={`margin_bottom`}>
            {roomsRecieved} of {totalRooms} created
          </div>
          {roomsRecieved == totalRooms && <JUNOButton {...closeButton} />}
        </>
      )}
    </motion.div>
  );
};

const wrapperVariants = {
  visible: {
    opacity: 1,
    display: 'flex',
  },
  hidden: {
    opacity: 0,
    display: 'none',
  },
};

export default SendToBreakoutsLoader;
